
import { useState, useCallback, useMemo, useEffect } from "react";
import { convertEnergyPreference } from "@/lib/energy/utils";
import type { EnergyBlockType } from "@/lib/energy/types";
import { useHostSettings, extractWakeTimeFromProfile } from "@/hooks/calendar-slots/use-host-settings";
import { DEFAULT_BOOKING_SETTINGS } from "@/lib/constants/booking-defaults";
import { parseISO, isValid } from "date-fns";
import { useBookingContext } from "@/contexts/BookingContext";

interface DebugSettings {
  startTime: string;
  endTime: string;
  bufferBefore: number;
  bufferAfter: number;
  minNoticeTime: number;
  energyPreference: EnergyBlockType;
  duration: number;
  maxPerDay: number;
  wakeTime: number;
}

export const useModernTemplateState = (meetingLink: any, urlParams: Record<string, any> = {}) => {
  // Get booking context for reschedule mode (optional - only if BookingProvider exists)
  let isRescheduleMode = false;
  let isCancelMode = false;
  let existingBooking = null;
  
  try {
    const bookingContext = useBookingContext();
    isRescheduleMode = bookingContext.isRescheduleMode;
    isCancelMode = bookingContext.isCancelMode;
    existingBooking = bookingContext.existingBooking;
  } catch (error) {
    // BookingProvider not found - this is fine for preview/edit pages
    // Use default values: all false/null
  }
  
  // Always start at contact step when in booking management mode (reschedule or cancel)
  // This shows the booking details first, then user can choose to reschedule (date picker) or cancel
  const initialStep = (isRescheduleMode || isCancelMode) ? "contact" : "date";
  
  const [step, setStep] = useState<"date" | "timeslot" | "contact">(initialStep);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    // In reschedule/cancel mode, initialize with existing booking date
    (isRescheduleMode || isCancelMode) && existingBooking 
      ? new Date(existingBooking.booking_date)
      : null
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(
    // In reschedule/cancel mode, initialize with existing booking time
    (isRescheduleMode || isCancelMode) && existingBooking 
      ? new Date(existingBooking.booking_date).toLocaleTimeString('en-US', { 
          hour: '2-digit',
          minute: '2-digit',
          hour12: false 
        })
      : null
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    existingBooking?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Use the centralized hook to get host settings
  const { hostSettings, loading: hostSettingsLoading } = useHostSettings(
    meetingLink?.user_id,
    {
      startTime: DEFAULT_BOOKING_SETTINGS.START_TIME,
      endTime: DEFAULT_BOOKING_SETTINGS.END_TIME,
      bufferBefore: DEFAULT_BOOKING_SETTINGS.BUFFER_BEFORE,
      bufferAfter: DEFAULT_BOOKING_SETTINGS.BUFFER_AFTER,
      minNoticeTime: DEFAULT_BOOKING_SETTINGS.MIN_NOTICE_TIME,
    }
  );

  // Extract wakeTime from host settings
  const wakeTime = useMemo(() => 
    hostSettings?.wakeTime || hostSettings?.userEnergySettings?.wakeTime ? 
    extractWakeTimeFromProfile(hostSettings?.userEnergySettings ? { energy_profile: hostSettings.userEnergySettings } : null) : 
    DEFAULT_BOOKING_SETTINGS.WAKE_TIME,
    [hostSettings]
  );

  // Default settings for the meeting
  const debugSettings = useMemo(() => ({
    startTime: DEFAULT_BOOKING_SETTINGS.START_TIME,
    endTime: DEFAULT_BOOKING_SETTINGS.END_TIME,
    bufferBefore: DEFAULT_BOOKING_SETTINGS.BUFFER_BEFORE,
    bufferAfter: DEFAULT_BOOKING_SETTINGS.BUFFER_AFTER,
    minNoticeTime: DEFAULT_BOOKING_SETTINGS.MIN_NOTICE_TIME,
    energyPreference: convertEnergyPreference(meetingLink?.energy_preference || DEFAULT_BOOKING_SETTINGS.ENERGY_PREFERENCE) as EnergyBlockType,
    duration: urlParams.duration || parseInt(meetingLink?.duration) || DEFAULT_BOOKING_SETTINGS.DURATION,
    maxPerDay: meetingLink?.max_per_day || DEFAULT_BOOKING_SETTINGS.MAX_PER_DAY,
    wakeTime
  }), [meetingLink?.energy_preference, meetingLink?.duration, meetingLink?.max_per_day, urlParams.duration, wakeTime]);

  // Parse preselected date from URL params
  const preselectedDate = useMemo(() => {
    if (urlParams.date) {
      const date = parseISO(urlParams.date);
      return isValid(date) ? date : null;
    }
    return null;
  }, [urlParams.date]);


  // Handler for when a date is selected
  const handleDateSelected = useCallback((date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      setStep("timeslot");
    }
  }, []);

  // Handler for when a time slot is selected
  const handleTimeSelected = useCallback((time: string) => {
    setSelectedTime(time);
    setStep("contact");
  }, []);

  // Handler for going back to previous step
  const handleBack = useCallback(() => {
    if (step === "contact") {
      setStep("timeslot");
    } else if (step === "timeslot") {
      setStep("date");
    }
  }, [step]);

  // Handler for form submission
  const handleFormSubmit = useCallback(async (formData: any) => {
    console.log("Form submitted:", formData);
  }, []);

  // Update settings handler for debug mode
  const handleUpdateSettings = useCallback((settings: Partial<DebugSettings>) => {
    console.log("Settings updated:", settings);
  }, []);

  // Update step when booking mode changes
  useEffect(() => {
    if (isCancelMode) {
      setStep("contact");
    }
    // For reschedule mode, don't automatically advance to contact step
    // Let the user select a new date/time first
  }, [isCancelMode]);

  return {
    step,
    selectedDate,
    selectedTime,
    selectedTimezone,
    debugSettings,
    preselectedDate,
    hostSettingsLoading,
    setSelectedTimezone,
    handleDateSelected,
    handleTimeSelected,
    handleBack,
    handleFormSubmit,
    handleUpdateSettings
  };
};
